/**
 * The workspace page's last-used view, sort and filters, per user, per
 * workspace: ONE choice that every collection (and the root) shares, so
 * moving around the workspace keeps the same view. Scope is not part of it;
 * it describes where, not how, and stays with each location's URL.
 *
 * One persisted map keyed `${userId}:${workspaceId}`, the in-store
 * partitioning `dataGridPrefsStore` uses: the localStorage key stays fixed, so
 * switching user or workspace never re-creates the store, and two people
 * sharing a browser never see each other's choices.
 *
 * Each value is the choice as a URL query string (`view=list&sort=modified`),
 * written and read by `useWorkspaceViewParams` with the same field-by-field
 * parser as the URL, so a stale or hand-edited entry degrades the way a stale
 * link does instead of throwing.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type WorkspaceView = 'folders' | 'cards' | 'list';

interface WorkspaceViewPrefsState {
  readonly choices: Record<string, string>;
  setChoice: (key: string, choice: string) => void;
}

export function workspaceViewPrefsKey(userId: string, workspaceId: string): string {
  return `${userId}:${workspaceId}`;
}

export const useWorkspaceViewPrefsStore = create<WorkspaceViewPrefsState>()(
  persist(
    (set) => ({
      choices: {},
      setChoice: (key, choice) =>
        set((state) => ({ choices: { ...state.choices, [key]: choice } })),
    }),
    {
      name: 'workspace-view-prefs',
      // Version 0 remembered a view per folder (`{ views }`). There is no one
      // folder's view to promote to the workspace-wide choice, so it is
      // dropped: the next visit starts from the defaults.
      // Version 1 remembered the Equipment page's own URL (`kinds=equipment`
      // alone, with the page's `view=list`) as the choice, so every collection
      // opened filtered to equipment in a list. Both are the page's, so both are
      // dropped; the rest of the choice (sort, other filters) is kept.
      version: 2,
      migrate: (persisted, version) => {
        if (version === 0) return { choices: {} };
        const { choices = {} } = (persisted ?? {}) as { choices?: Record<string, string> };
        return {
          choices: Object.fromEntries(
            Object.entries(choices).map(([key, choice]) => {
              const params = new URLSearchParams(choice);
              if (params.get('kinds') === 'equipment') {
                params.delete('kinds');
                if (params.get('view') === 'list') params.delete('view');
              }
              return [key, params.toString()];
            }),
          ),
        };
      },
      partialize: (state) => ({ choices: state.choices }),
    },
  ),
);
